import { CampaignBase } from '../../models';
import { TK } from '../../store/translations/translationKeys';

const AllowedPlaceholders = ['client-name', 'company-name', 'professional-name', 'booking-link', 'unsubscribe-link'] as const;

export const campaignBuilderStepCount = 4;

export const clampCampaignBuilderStep = (step?: number): number => {
    if (!step || step < 1) return 1;
    if (step > campaignBuilderStepCount) return campaignBuilderStepCount;
    return step;
};

export const getCampaignBuilderStepKey = (step: number): TK => {
    switch (step) {
        case 1: return TK.campaignBuilderBasics;
        case 2: return TK.campaignBuilderAudience;
        case 3: return TK.campaignBuilderContent;
        case 4: return TK.campaignBuilderReview;
        default: return TK.campaignBuilderBasics;
    }
};

export const normalizeCampaignDraft = (campaign: CampaignBase): CampaignBase => ({
    ...campaign,
    name: campaign.name?.trim() ?? '',
    subject: campaign.subject?.trim() ?? '',
    content: campaign.content ?? '',
    internalDescription: campaign.internalDescription?.trim() || undefined,
    preheader: campaign.preheader?.trim() || undefined,
    senderName: campaign.senderName?.trim() || undefined,
    senderEmail: campaign.senderEmail?.trim() || undefined,
    recipientsClientsTagsToInclude: normalizeStringArray(campaign.recipientsClientsTagsToInclude),
    recipientsClientsIdsToInclude: normalizeStringArray(campaign.recipientsClientsIdsToInclude),
    recipientsClientsIdsToIgnore: normalizeStringArray(campaign.recipientsClientsIdsToIgnore),
    recipientsAdditionalEmails: normalizeStringArray(campaign.recipientsAdditionalEmails),
    builderStep: clampCampaignBuilderStep(campaign.builderStep),
    templateParameters: campaign.templateParameters?.map(parameter => ({
        componentType: parameter.componentType?.trim() ?? '',
        values: normalizeStringArray(parameter.values),
    })).filter(parameter => parameter.componentType.length > 0),
});

export const normalizeStringArray = (values?: string[]): string[] => Array.from(new Set((values ?? [])
    .map(value => value.trim())
    .filter(value => value.length > 0)));

const getCampaignDraftSnapshot = (campaign: CampaignBase): Record<string, unknown> => ({
    name: campaign.name?.trim() ?? '',
    subject: campaign.subject?.trim() ?? '',
    content: campaign.content ?? '',
    internalDescription: campaign.internalDescription?.trim() || undefined,
    preheader: campaign.preheader?.trim() || undefined,
    senderName: campaign.senderName?.trim() || undefined,
    senderEmail: campaign.senderEmail?.trim() || undefined,
    recipientsAllClients: campaign.recipientsAllClients ?? false,
    ignoreMarketingConsent: campaign.ignoreMarketingConsent ?? false,
    recipientsClientsTagsToInclude: normalizeStringArray(campaign.recipientsClientsTagsToInclude),
    recipientsClientsIdsToInclude: normalizeStringArray(campaign.recipientsClientsIdsToInclude),
    recipientsClientsIdsToIgnore: normalizeStringArray(campaign.recipientsClientsIdsToIgnore),
    recipientsAdditionalEmails: normalizeStringArray(campaign.recipientsAdditionalEmails),
    channel: campaign.channel,
    goal: campaign.goal ?? null,
    consentMode: campaign.consentMode,
    builderStep: clampCampaignBuilderStep(campaign.builderStep),
    includeClientsWithoutMarketingConsentSubmitted: campaign.includeClientsWithoutMarketingConsentSubmitted ?? false,
    templateId: campaign.templateId?.trim() || undefined,
    templateVersion: campaign.templateVersion?.trim() || undefined,
    templateLanguage: campaign.templateLanguage?.trim() || undefined,
    templateParameters: campaign.templateParameters?.map(parameter => ({
        componentType: parameter.componentType?.trim() ?? '',
        values: normalizeStringArray(parameter.values),
    })).filter(parameter => parameter.componentType.length > 0),
});

export const getCampaignDraftFingerprint = (campaign: CampaignBase): string => JSON.stringify(getCampaignDraftSnapshot(campaign));

export const getUnknownCampaignPlaceholders = (...fields: Array<string | undefined>): string[] => {
    const placeholders = fields
        .flatMap(field => [...(field?.matchAll(/\{([a-zA-Z][a-zA-Z0-9-_]*)\}/g) ?? [])].map(match => match[1]))
        .filter(Boolean);

    return Array.from(new Set(placeholders.filter(placeholder => !AllowedPlaceholders.includes(placeholder as (typeof AllowedPlaceholders)[number]))));
};

export const getAllowedCampaignPlaceholders = (): readonly string[] => AllowedPlaceholders;
